import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { DocumentFile } from "@shared/operations-schema";
import { useToast } from "@/hooks/use-toast";

export const useDocuments = (businessId: number | undefined, transactionId?: number) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Get all documents for business
  const documentsUrl = transactionId
    ? `/api/transactions/${transactionId}/documents`
    : `/api/businesses/${businessId}/documents`;
  const documentsQuery = useQuery<DocumentFile[], Error>({
    queryKey: [documentsUrl],
    enabled: !!businessId,
  });

  // Upload document
  const uploadDocumentMutation = useMutation({
    mutationFn: async ({ file, description, transactionId }: { 
      file: File, 
      description?: string, 
      transactionId?: number 
    }) => {
      if (!businessId) throw new Error("Business ID is required");

      const formData = new FormData();
      formData.append("file", file);
      if (description) formData.append("description", description);
      if (transactionId) formData.append("transactionId", transactionId.toString());

      const response = await fetch(`/api/businesses/${businessId}/documents`, {
        method: "POST",
        body: formData,
        credentials: "include"
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to upload document");
      }

      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/businesses/${businessId}/documents`] });
      
      toast({
        title: "Document uploaded",
        description: "The document was uploaded successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update document
  const updateDocumentMutation = useMutation({
    mutationFn: async ({ 
      documentId, 
      name, 
      description, 
      transactionId 
    }: { 
      documentId: number, 
      name?: string, 
      description?: string, 
      transactionId?: number | undefined 
    }) => {
      const response = await apiRequest("PATCH", `/api/documents/${documentId}`, {
        name, 
        description, 
        transactionId
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update document");
      }
      
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/businesses/${businessId}/documents`] });
      
      toast({
        title: "Document updated",
        description: "The document details were updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete document
  const deleteDocumentMutation = useMutation({
    mutationFn: async (documentId: number) => {
      const response = await apiRequest("DELETE", `/api/documents/${documentId}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete document");
      }
      
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/businesses/${businessId}/documents`] });
      
      toast({
        title: "Document deleted",
        description: "The document was deleted successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Delete failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Generate download URL
  const getDownloadUrl = (documentId: number) => {
    return `/api/documents/${documentId}/download`;
  };

  return {
    documents: documentsQuery.data ?? [],
    isLoading: documentsQuery.isLoading,
    error: documentsQuery.error,
    uploadDocument: uploadDocumentMutation.mutate,
    isUploading: uploadDocumentMutation.isPending,
    updateDocument: updateDocumentMutation.mutate,
    isUpdating: updateDocumentMutation.isPending,
    deleteDocument: deleteDocumentMutation.mutate,
    isDeleting: deleteDocumentMutation.isPending,
    getDownloadUrl
  };
};
